import type { Post } from "./types";
import heroKfm from "@/assets/news/lana-pengar-skuld-kronofogden.jpg";

export const POSTS_16: Record<string, Post> = {
  "lana-pengar-med-skuld-hos-kronofogden": {
    title: "Låna pengar med skuld hos Kronofogden – vad som gäller 2026",
    intro:
      "Att låna pengar med skuld hos Kronofogden känns ofta som en stängd dörr. Vi förstår varför – avslagen kommer snabbt och ingen förklarar riktigt varför. Här går vi igenom vad som faktiskt gäller 2026: vilka långivare som kan säga ja, vad skillnaden är mellan ett pågående och ett avslutat mål, vad ett lån kostar i det läget – och vilka gratisalternativ som nästan alltid är bättre än ett nytt dyrt lån.",
    author: "Erik Lindqvist",
    authorTitle: "finansiell skribent, 9 år inom konsumentkrediter",
    published: "3 september 2026",
    updated: "3 september 2026",
    heroSrc: heroKfm,
    heroAlt: "Man går igenom räkningar vid köksbordet och funderar på att låna pengar med skuld hos Kronofogden",
    topLenderId: "northmill",
    bottomLenderId: "credifi",
    topLenderTitle: "Populärast vid tuff kredithistorik",
    bottomLenderTitle: "Vår rekommendation för snabb prövning utan UC",
    reviewsLenderId: "credifi",
    cta: { to: "/lan-utan-uc", label: "Se långivare som prövar utan UC" },
    calculator: {
      title: "Räkna på lån med skuld hos Kronofogden",
      intro:
        "Innan du ansöker: se vad lånet faktiskt kostar. Dra i reglagen så får du månadskostnad, räntekostnad, total lånekostnad och effektiv ränta.",
      note:
        "Månadskostnaden styrs av lånebelopp, löptid och nominell ränta, men det är den effektiva räntan som visar din verkliga kostnad eftersom uppläggningsavgift och aviavgift räknas in. Har du redan ett utmätningsbeslut minskar ditt utrymme varje månad – räkna därför alltid på hela lånekostnaden, inte bara på månadskostnaden.",
      amount: 15000,
      minAmount: 1000,
      maxAmount: 50000,
      months: 24,
      maxMonths: 60,
      rate: 24.9,
      minRate: 5,
      maxRate: 39,
      setupFee: 395,
      invoiceFee: 29,
    },
    faq: [
      {
        q: "Kan man låna pengar med skuld hos Kronofogden?",
        a: "Det är svårt men inte helt omöjligt. Med ett pågående mål och aktiv utmätning säger nästan alla nej. Är skulden reglerad och anmärkningen på väg att försvinna gör flera nischlångivare en individuell bedömning.",
      },
      {
        q: "Hur länge syns en skuld hos Kronofogden?",
        a: "En betalningsanmärkning för en privatperson ligger kvar i tre år från registreringen, även om du betalat. Skuldsaldot hos Kronofogden försvinner däremot när skulden är betald.",
      },
      {
        q: "Får jag låna om jag har skuldsanering?",
        a: "Nej. Under en pågående skuldsanering får du enligt lag inte ta nya krediter, och långivare beviljar inte heller lån. Kontakta i stället din handläggare om ekonomin inte går ihop.",
      },
      {
        q: "Vad händer med lånet om jag får utmätning?",
        a: "Utmätning görs på din lön efter förbehållsbeloppet, alltså det du behöver för boende och mat. Ett nytt lån minskar inte utmätningen – däremot ökar det din totala skuld.",
      },
      {
        q: "Finns det långivare som inte tar kreditupplysning alls?",
        a: "Nej. Kreditprövning är obligatorisk enligt konsumentkreditlagen. Den som lovar lån helt utan prövning bryter mot lagen eller är ett bedrägeriförsök – kontrollera alltid tillståndet hos Finansinspektionen.",
      },
      {
        q: "Vad kostar ett lån på 15 000 kr i det här läget?",
        a: "Med 24,9 procent nominell ränta, 395 kr uppläggningsavgift och 29 kr per avi blir månadskostnaden cirka 830 kr på 24 månader. Totalt betalar du då runt 19 900 kr, vilket motsvarar en effektiv ränta kring 31 procent.",
      },
      {
        q: "Vart vänder jag mig om jag inte klarar betalningarna?",
        a: "Till kommunens budget- och skuldrådgivning, som är kostnadsfri och opartisk. De kan hjälpa dig att göra en betalningsplan, kontakta fordringsägare och bedöma om skuldsanering är rätt väg.",
      },
    ],
    blocks: [
      { t: "h2", text: "Låna pengar med skuld hos Kronofogden – kort svar" },
      { t: "p", text: "Att låna pengar med skuld hos Kronofogden är svårt, men läget avgörs av om målet är pågående eller avslutat. Med aktiv utmätning nekas i princip alla ansökningar. Är skulden betald och du har regelbunden inkomst gör flera nischlångivare en individuell bedömning i stället för att sortera bort dig automatiskt." },
      { t: "p", text: "Vi vet att det här sällan handlar om oansvarighet. Sjukskrivning, separation, en arbetslös period – livet ställer till det för väldigt många. Därför är den här guiden skriven utan pekpinnar, men med raka siffror." },
      {
        t: "stats",
        items: [
          { label: "Anmärkning ligger kvar", value: "3 år" },
          { label: "Typisk ränta vid tuff historik", value: "22–29 %" },
          { label: "Kostnadstak enligt lag", value: "Max 100 % av lånet" },
        ],
      },

      { t: "h2", text: "Skillnaden mellan skuld, anmärkning och utmätning" },
      { t: "p", text: "Tre begrepp blandas ofta ihop, men de betyder helt olika saker för din chans att bli beviljad." },
      { t: "h3", text: "Skuldsaldo hos Kronofogden" },
      { t: "p", text: "Ett aktivt saldo betyder att ärendet pågår. Det är den enskilt starkaste avslagsfaktorn hos i stort sett alla långivare." },
      { t: "h3", text: "Betalningsanmärkning" },
      { t: "p", text: "Anmärkningen registreras hos kreditupplysningsbolagen och ligger kvar i tre år, även efter att du betalat. Den försämrar din kreditvärdighet men stänger inte alla dörrar." },
      { t: "h3", text: "Utmätning" },
      { t: "p", text: "Vid löneutmätning drar arbetsgivaren ett belopp direkt från lönen. Kvar får du ditt förbehållsbelopp. Eftersom kvarvarande utrymme då är litet nekas nya krediter nästan alltid." },

      { t: "h2", text: "Varför långivare säger nej – och när de kan säga ja" },
      { t: "p", text: "Långivaren är enligt konsumentkreditlagen skyldig att bedöma din återbetalningsförmåga. Ett aktivt mål hos Kronofogden är ett tydligt tecken på att den förmågan är ansträngd, och då får banken helt enkelt inte bevilja lånet." },
      {
        t: "ul",
        items: [
          "Aktivt skuldsaldo – nej i princip alltid.",
          "Pågående löneutmätning – nej i princip alltid.",
          "Betald skuld men kvarvarande anmärkning – individuell bedömning.",
          "Anmärkning äldre än två år och stabil inkomst – klart bättre chans.",
          "Pågående skuldsanering – nej enligt lag.",
        ],
      },

      { t: "h2", text: "Långivare som gör en individuell bedömning" },
      { t: "p", text: "Flera nischlångivare använder Creditsafe eller Dun & Bradstreet i stället för UC och tittar mer på din nuvarande inkomst än på historiken. Det är inget löfte om ja – men prövningen är mer nyanserad." },
      { t: "p", text: "Vill du se hela listan finns den på vår sida om [lån utan UC](/lan-utan-uc), och för mindre belopp i vår guide om [lån utan UC med många förfrågningar](/nyhet/lan-utan-uc-manga-forfragningar)." },

      { t: "h2", text: "Vad ett lån kostar i det här läget" },
      { t: "p", text: "Räntan sätts efter risk, och med tuff kredithistorik hamnar du nära räntetaket. Nedan visar vi vad 15 000 kr kostar med 24,9 procent nominell ränta, 395 kr i uppläggningsavgift och 29 kr per avi." },
      {
        t: "table",
        caption: "Räkneexempel: 15 000 kr, annuitetslån, 24,9 % nominell ränta",
        head: ["Löptid", "Månadskostnad", "Räntekostnad", "Total kostnad", "Effektiv ränta"],
        rows: [
          ["12 mån", "1 456 kr", "2 077 kr", "17 472 kr", "37,2 %"],
          ["24 mån", "830 kr", "4 125 kr", "19 915 kr", "31,4 %"],
          ["36 mån", "631 kr", "6 316 kr", "22 715 kr", "29,6 %"],
          ["48 mån", "537 kr", "8 583 kr", "25 770 kr", "28,7 %"],
        ],
      },
      { t: "p", text: "Skillnaden mellan tolv och 48 månader är drygt 8 000 kr. Räkna på dina egna siffror i vår [lånekalkyl](/guider/lanekalkyl) innan du signerar något." },

      { t: "h2", text: "Räntetak och kostnadstak – ditt skydd i lagen" },
      { t: "p", text: "Den nominella räntan får aldrig överstiga Riksbankens referensränta plus 20 procentenheter. De samlade kostnaderna för ränta, avgifter och dröjsmålsränta får aldrig överstiga lånebeloppet. Lånar du 15 000 kr kan kostnaden alltså aldrig bli mer än 15 000 kr, oavsett hur länge skulden ligger kvar." },
      {
        t: "box",
        tone: "info",
        title: "Det här gäller alltid",
        items: [
          "14 dagars ångerrätt enligt konsumentkreditlagen.",
          "Rätt att lösa lånet i förtid utan extra avgift.",
          "Kreditprövning är obligatorisk – ingen seriös långivare hoppar över den.",
          "Långivaren måste ha tillstånd från Finansinspektionen.",
        ],
      },

      { t: "h2", text: "Så ökar du chansen att bli beviljad" },
      {
        t: "steps",
        title: "Fem steg som gör verklig skillnad",
        items: [
          "Betala av det aktiva skuldsaldot först – det väger tyngre än allt annat.",
          "Begär ett utdrag från Kronofogden och kontrollera att uppgifterna stämmer.",
          "Vänta minst en månad mellan ansökningar så att förfrågningarna inte staplas.",
          "Ansök om ett realistiskt belopp – ju mindre, desto större chans.",
          "Lägg till en medsökande med egen inkomst om det är möjligt.",
        ],
      },

      { t: "h2", text: "Vanliga misstag när det är akut" },
      {
        t: "box",
        tone: "warning",
        title: "Fyra fällor att undvika",
        items: [
          "Att skicka in ansökningar hos tio långivare samma kväll.",
          "Att ta ett nytt lån för att betala av ett gammalt – det förlänger bara problemet.",
          "Att lita på annonser som lovar garanterat ja oavsett kreditvärdighet.",
          "Att betala en avgift i förskott för att få ett lån – det är alltid bedrägeri.",
        ],
      },

      { t: "h2", text: "Gratisalternativen som ofta är bättre" },
      {
        t: "ul",
        items: [
          "Kommunens budget- och skuldrådgivning – kostnadsfri, opartisk och finns i varje kommun.",
          "Avbetalningsplan direkt hos fordringsägaren, ofta räntefri.",
          "Anstånd hos Kronofogden om din situation tillfälligt förändrats.",
          "Ansökan om skuldsanering om skulderna är omöjliga att betala inom överskådlig tid.",
          "Samla dyra krediter i ett [samlingslån](/samla-lan) om du fortfarande är kreditvärdig.",
        ],
      },

      { t: "h2", text: "Skuldsanering – när det är rätt väg" },
      { t: "p", text: "Skuldsanering innebär att du under normalt fem år betalar allt du kan, varefter resten skrivs av. Det kräver att du är så skuldsatt att du inte rimligen kan betala inom överskådlig tid. Ansökan är gratis och görs hos Kronofogden." },
      { t: "p", text: "Under skuldsaneringen får du inte ta nya krediter. Det känns hårt, men för många är det den enda vägen tillbaka till en ekonomi utan ständig oro." },

      { t: "h2", text: "För- och nackdelar med att låna i det här läget" },
      {
        t: "proscons",
        pros: [
          "Kan lösa ett akut och tidsbegränsat problem",
          "Vissa långivare prövar utan UC",
          "Utbetalning ofta samma dag",
          "Rätt att lösa lånet i förtid när ekonomin vänder",
        ],
        cons: [
          "Hög effektiv ränta ökar den samlade skulden",
          "Risk att förvärra en redan pressad situation",
          "Låg beviljandegrad ger många nekade förfrågningar",
          "Löser sällan grundproblemet",
        ],
      },

      { t: "h2", text: "Så granskar vi långivarna" },
      { t: "p", text: "Vi väger ränta, avgifter, utbetalningstid, transparens i villkoren, tillstånd hos Finansinspektionen och kundomdömen. Ingen långivare kan köpa sig till en placering. Läs mer om [hur vi granskar](/om-oss/hur-vi-granskar) och om vår [redaktion](/om-oss/redaktion)." },

      { t: "h2", text: "Sammanfattning" },
      { t: "p", text: "Med ett pågående mål hos Kronofogden är ett nytt lån varken realistiskt eller särskilt klokt. Är skulden reglerad finns det däremot långivare som gör en individuell bedömning. Räkna alltid på total kostnad, låna minsta möjliga belopp – och ta hjälp av budget- och skuldrådgivningen innan du signerar något." },

      { t: "h2", text: "Så påverkas din vardagsekonomi under ett Kronofogde-ärende" },
      { t: "p", text: "Ett ärende hos Kronofogden märks långt utanför själva skulden. Många upptäcker det först när ett mobilabonnemang nekas, när hyresvärden gör en kreditupplysning eller när elbolaget kräver förskottsbetalning. Det är inte ett straff – det är helt enkelt så kreditupplysningssystemet fungerar." },
      { t: "p", text: "Samtidigt finns det mer utrymme än många tror. Du har rätt till ett bankkonto, du får byta bank, du kan teckna hemförsäkring och du kan i de flesta fall behålla ditt jobb. Att veta vad som faktiskt gäller gör det lättare att planera i stället för att gissa." },
      { t: "h3", text: "Förbehållsbeloppet – det du alltid får behålla" },
      { t: "p", text: "Vid löneutmätning räknar Kronofogden fram ett förbehållsbelopp som ska täcka mat, kläder, hygien, telefon och boende. Först det som blir över utmäts. Ändras din situation – högre hyra, ny försörjningsbörda, sjukdom – kan du begära omprövning. Det är gratis och görs skriftligt." },
      { t: "h3", text: "Vad som händer om du tar ett nytt lån ändå" },
      { t: "p", text: "Ett nytt lån ökar inte ditt förbehållsbelopp. Kronofogden tar alltså inte hänsyn till din nya månadskostnad, vilket innebär att lånet ska betalas ur pengar du redan behöver till mat och hyra. Det är den enskilt vanligaste orsaken till att en skuld blir två." },

      { t: "h2", text: "Så ser kreditprövningen ut från långivarens sida" },
      { t: "p", text: "När du signerar med BankID hämtas en upplysning där långivaren ser inkomstuppgifter, antal förfrågningar, eventuella anmärkningar och om du finns registrerad hos Kronofogden. Systemet räknar sedan fram ett riskvärde, och det värdet avgör både om du får ja och vilken ränta du erbjuds." },
      {
        t: "table",
        caption: "Vad som väger tyngst i bedömningen",
        head: ["Faktor", "Påverkan", "Går att förbättra"],
        rows: [
          ["Aktivt skuldsaldo", "Mycket stor", "Ja – betala av"],
          ["Pågående utmätning", "Mycket stor", "Ja – när skulden är löst"],
          ["Antal anmärkningar", "Stor", "Nej – tiden avgör"],
          ["Antal kreditförfrågningar", "Medel", "Ja – sprid ut ansökningarna"],
          ["Inkomstens storlek", "Stor", "Ja – på sikt"],
          ["Anställningsform", "Medel", "Ja – fast anställning väger tyngre"],
        ],
      },
      { t: "p", text: "Det tröstande i tabellen är att flera av faktorerna faktiskt går att påverka. En reglerad skuld, tre lugna månader utan nya förfrågningar och en stabil inkomst kan förändra bilden mer än man tror." },

      { t: "h2", text: "Vanliga frågor från läsare i samma situation" },
      { t: "h3", text: "Kan jag byta bank med skuld hos Kronofogden?" },
      { t: "p", text: "Ja. Du har enligt lag rätt till ett betalkonto med grundläggande funktioner hos i princip alla banker, oavsett skuldsaldo. Kreditkort och kontokredit är däremot något banken får neka." },
      { t: "h3", text: "Kan jag hyra bostad?" },
      { t: "p", text: "Det är svårare men inte omöjligt. Många privata hyresvärdar nekar vid anmärkning, medan kommunala bostadsbolag oftare gör en individuell bedömning, särskilt om skulden är reglerad och du kan visa inkomst." },
      { t: "h3", text: "Kan jag få tillbaka min kreditvärdighet?" },
      { t: "p", text: "Ja. Anmärkningen försvinner tre år efter registreringen och skuldsaldot när skulden är betald. Därefter byggs kreditvärdigheten upp igen genom att räkningar betalas i tid och att du inte samlar på dig nya förfrågningar." },

      { t: "h2", text: "En realistisk plan för de närmaste tolv månaderna" },
      {
        t: "steps",
        title: "Steg för steg tillbaka",
        items: [
          "Begär ett kostnadsfritt utdrag från Kronofogden och lista varje mål.",
          "Kontakta budget- och skuldrådgivningen i din kommun – det kostar ingenting.",
          "Gör en enkel månadsbudget och identifiera minst en utgift att pausa.",
          "Kontakta fordringsägarna och föreslå en avbetalningsplan du faktiskt klarar.",
          "Betala av det minsta målet först – varje avslutat mål är ett steg framåt.",
          "Bygg upp en buffert på 5 000 kr innan du ens överväger nya krediter.",
        ],
      },
      { t: "p", text: "Det här är ingen quick fix, och vi ska inte låtsas något annat. Men mönstret hos dem som tar sig ur är påfallande likt: ordning på siffrorna, hjälp utifrån och inga nya lån under tiden." },

      { t: "h2", text: "Så känner du igen ett bedrägeri" },
      { t: "p", text: "När sökningarna blir desperata dyker annonserna upp: garanterat lån, inget UC, inga frågor. Seriösa aktörer arbetar aldrig så. Kontrollera alltid företaget i Finansinspektionens register innan du lämnar några uppgifter." },
      {
        t: "box",
        tone: "warning",
        title: "Varningssignaler",
        items: [
          "Avgift som ska betalas i förskott för att lånet ska beviljas.",
          "Kontakt enbart via Messenger, WhatsApp eller sms.",
          "Löfte om lån utan kreditprövning.",
          "Ingen adress, inget organisationsnummer och inga fullständiga villkor.",
        ],
      },

      { t: "h2", text: "Olika typer av skulder – och hur de behandlas" },
      { t: "p", text: "Alla skulder hos Kronofogden väger inte lika tungt. Skillnaden mellan en obetald parkeringsbot och en misskött kredit är stor både i storlek och i hur en långivare tolkar den." },
      {
        t: "table",
        caption: "Vanliga skuldtyper och hur de bedöms",
        head: ["Skuldtyp", "Vanlig storlek", "Hur långivare ser på den"],
        rows: [
          ["Obetald faktura eller bot", "500–5 000 kr", "Mildast – ofta en glömska"],
          ["Skatteskuld", "5 000–50 000 kr", "Allvarlig, men tydligt avgränsad"],
          ["Missköt kredit eller snabblån", "10 000–100 000 kr", "Tyngst – rör återbetalningsförmågan"],
          ["Underhållsstöd", "Varierar", "Bedöms individuellt"],
          ["Hyresskuld", "10 000–60 000 kr", "Allvarlig, påverkar även bostad"],
        ],
      },
      { t: "p", text: "En liten, betald skuld från en glömd faktura stänger sällan alla dörrar. Flera missköta krediter under kort tid gör det däremot, eftersom mönstret säger något om ekonomin i stort." },

      { t: "h2", text: "Så länge dröjer det innan du är kreditvärdig igen" },
      { t: "p", text: "Tidslinjen är mer förutsägbar än många tror. Skuldsaldot försvinner när skulden är betald, men anmärkningen ligger kvar i tre år från registreringsdatumet. Under tiden förbättras din situation gradvis." },
      {
        t: "ul",
        items: [
          "**Månad 0–6:** skulden betalas, saldot avförs. Nya lån är fortfarande mycket svåra.",
          "**Månad 6–18:** vissa nischlångivare börjar göra individuella bedömningar.",
          "**Månad 18–36:** chansen ökar tydligt om inkomsten är stabil och inga nya förfrågningar tillkommit.",
          "**Efter 36 månader:** anmärkningen försvinner och du bedöms som vilken sökande som helst.",
        ],
      },
      { t: "p", text: "Det viktigaste under de här månaderna är tråkigt men effektivt: betala i tid, låt bli nya krediter och låt tiden göra sitt jobb." },

      { t: "h2", text: "Om någon erbjuder sig att låna åt dig" },
      { t: "p", text: "Ett vanligt förslag i det här läget är att en anhörig tar lånet i sitt namn. Det låter enkelt men är juridiskt entydigt: den som står på avtalet är också den som är betalningsskyldig, oavsett vem som använder pengarna." },
      { t: "p", text: "Vill ni ändå gå den vägen, skriv ett enkelt skuldebrev mellan er med belopp, återbetalningsplan och datum. Det skyddar relationen minst lika mycket som ekonomin." },

      { t: "h2", text: "Källor" },
      {
        t: "ul",
        items: [
          "[Kronofogden](https://www.kronofogden.se) – skuldsaldo, utmätning och skuldsanering",
          "[Konsumentverket](https://www.konsumentverket.se) – räntetak, kostnadstak och ångerrätt",
          "[Finansinspektionen](https://www.fi.se) – tillståndsregister och tillsyn",
          "[Hallå konsument](https://www.hallakonsument.se) – oberoende vägledning",
        ],
      },
    ],
  },
};
